import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import TIcon from "../../components/ui/TIcon";
import {
  BroadcastSlot,
  IS_PLACEHOLDER_CONTENT,
  LiveMatch,
  LiveStream,
  fetchFeaturedMatch,
  fetchSchedule,
  fetchStreams,
} from "../../mocks/content";
import {
  bottomBarState,
  loaderState,
  showBottomBarState,
} from "../../states/common";

/** Màn 03 — Livestream.
 *  Toàn bộ dữ liệu đến từ mocks/content.ts: backend chưa có endpoint phát
 *  trực tiếp. Giao diện đã dựng đúng thiết kế, chỉ cần đổi nguồn khi có API. */
const initials = (name: string) =>
  name
    .replace(/[^\p{L}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const LivePage = () => {
  const [, setLoader] = useRecoilState(loaderState);
  const [, setActive] = useRecoilState(bottomBarState);
  const [, setBottomBar] = useRecoilState(showBottomBarState);

  const [match, setMatch] = useState<LiveMatch | null>(null);
  const [streams, setStreams] = useState<LiveStream[]>([]);
  const [schedule, setSchedule] = useState<BroadcastSlot[]>([]);

  useEffect(() => {
    document.title = "Biadi — Livestream";
    setActive("live");
    setBottomBar(true);
    const load = async () => {
      setLoader(true);
      setMatch(await fetchFeaturedMatch());
      setStreams(await fetchStreams());
      setSchedule(await fetchSchedule());
      setLoader(false);
    };
    load();
  }, []);

  const liveCount = streams.filter((s) => s.isLive).length;
  const raceTo = match ? Number(match.format.replace(/\D/g, "")) || 11 : 11;

  return (
    <>
      <header className="page-head">
        <h1>LIVESTREAM</h1>
        <span className="live-label">
          <span className="live-dot" />
          {liveCount} LUỒNG
        </span>
      </header>

      {/* ── Khung phát ── */}
      <div className="player bleed">
        <div className="tags">
          <span className="ui-badge">
            <span className="live-dot" style={{ background: "#F2F3EF" }} />
            LIVE
          </span>
          <span className="views">
            <TIcon name="eye" /> 12.480
          </span>
        </div>
        <button type="button" className="play-btn" aria-label="Phát">
          <span>
            <TIcon name="player-play-filled" />
          </span>
        </button>
      </div>

      {/* ── Bảng tỷ số ── */}
      {match ? (
        <section className="ui-card scoreboard">
          <div className="head">
            <span className="round">
              {match.round.toUpperCase()} · {match.format.toUpperCase()}
            </span>
            <span className="game">{match.frame}</span>
          </div>

          <div className="players">
            <div className="side">
              <span className="avatar" style={{ width: 34, height: 34 }}>
                {initials(match.homeName)}
              </span>
              <div>
                <div className="p-name">{match.homeName}</div>
                <div className="p-sub">{match.homeRank}</div>
              </div>
            </div>

            <div className="score">
              <span
                className={`n${
                  match.homeScore > match.awayScore ? " lead" : ""
                }`}
              >
                {match.homeScore}
              </span>
              <span className="sep">—</span>
              <span
                className={`n${
                  match.awayScore > match.homeScore ? " lead" : ""
                }`}
              >
                {match.awayScore}
              </span>
            </div>

            <div className="side right">
              <div style={{ textAlign: "right" }}>
                <div className="p-name">{match.awayName}</div>
                <div className="p-sub">{match.awayRank}</div>
              </div>
              <span className="avatar" style={{ width: 34, height: 34 }}>
                {initials(match.awayName)}
              </span>
            </div>
          </div>

          <div className="rack-bar">
            {Array.from({ length: raceTo }).map((_, i) => (
              <span
                key={i}
                className={i < match.homeScore + match.awayScore ? "on" : ""}
              />
            ))}
          </div>

          <span className="note">{match.note}</span>
        </section>
      ) : null}

      {/* ── Đang phát ── */}
      <section className="section">
        <div className="section-head">
          <h2>ĐANG PHÁT</h2>
        </div>
        <div className="grid-2">
          {streams.map((s) => (
            <button key={s.id} type="button" className="stream-card">
              {s.isLive ? (
                <div className="thumb">
                  <img src={s.thumbnail} alt="" />
                  <span className="ui-badge">LIVE</span>
                </div>
              ) : (
                <div className="thumb upcoming">
                  Sắp phát
                  <br />
                  {s.startAt}
                </div>
              )}
              <span className="title">{s.title}</span>
              <span className="meta">
                {s.isLive ? (
                  <>
                    <TIcon name="eye" /> {s.viewers.toLocaleString("vi-VN")} đang
                    xem
                  </>
                ) : (
                  <>
                    <TIcon name="bell" /> Nhận thông báo
                  </>
                )}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Lịch phát sóng ── */}
      <section className="section">
        <div className="section-head">
          <h2>LỊCH PHÁT SÓNG</h2>
        </div>
        <div className="ui-card list">
          {schedule.map((slot) => (
            <div key={slot.id} className="list-row schedule-row">
              <span className="time">{slot.time}</span>
              <span className="what">{slot.title}</span>
              <span className="channel-tag">{slot.channel}</span>
            </div>
          ))}
        </div>
      </section>

      {IS_PLACEHOLDER_CONTENT ? (
        <p className="caption">
          Màn hình đang chạy dữ liệu mẫu. Cần endpoint livestream (luồng đang
          phát, tỷ số trực tiếp, lịch phát sóng) để nối dữ liệu thật.
        </p>
      ) : null}
    </>
  );
};

export default LivePage;
